import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Components
import Spinner from "../Components/Spinner";
import VehicleFiltersComponent from "../Components/Filters/VehicleFiltersComponent";
import { ToastComponent } from "../Components/ToastComponent";

// Services
import { downloadService } from "../../Services/download.service";
import GridReport from "../Components/Grid/GridReport";
import posthog from "../../Services/posthog";

export default function ReportPage({
  pageTitle,
  reportServiceFunction,
  pageGrid,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch(); // eslint-disable-next-line
  dispatch({ type: "title", value: pageTitle });

  const [toastParams, setToastParams] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [gridInfo, setGridInfo] = useState(null);
  const isVehiclePositionsGrid = pageGrid === "gridVehiclePositions";
  const isProtocolsGrid = pageGrid === "gridProtocols";

  // Identifica a chamada mais recente para descartar respostas de
  // requisições antigas (ex: usuário clica em "Exibir" mais de uma vez).
  const requestIdRef = useRef(0);

  const handleClearGrid = () => {
    setGridInfo(null);
  };

  // Chamado pelo GridReport somente quando a montagem do grid (Syncfusion
  // ou tabela simples) termina — só então o spinner é encerrado.
  const handleGridRendered = () => {
    setIsLoading(false);
  };

  const handleGenerateReport = async (
    groupId,
    vehicleId,
    initDateTime,
    endDateTime,
    durationTime,
    fileType,
    speed,
    download,
    driverId,
    eventType,
    userId
  ) => {

    if (validatePageFilters(pageTitle, vehicleId, userId)) {
      // Marca esta chamada como a mais recente.
      const currentRequestId = ++requestIdRef.current;

      if (download) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("REPORTS.REPORT_REQUEST"),
            body: "Processando download do relat\u00f3rio...",
            color: "Warning",
          })
        );
      } else {
        setToastParams(JSON.stringify({ show: false }));
      }

      setIsLoading(true);

      await new Promise((resolve) => setTimeout(resolve, 0));

      // Enquanto a resposta não chega, ou o grid resultante ainda não
      // terminou de montar, mantém o spinner (handleGridRendered encerra).
      let willMountGrid = false;

      try {
        const payload = {
          groupId,
          vehicleId,
          dateTimeScheduleStart: initDateTime,
          dateTimeScheduleEnd: endDateTime,
          durationTime,
          fileType,
          speed,
          driverId: pageGrid == "gridJourneyWork" ? driverId : 0,
          eventType,
          userId,
          // pageSize: 9999,
          // pageNumber: 1
        };

        if (isVehiclePositionsGrid) {
          delete payload.pageNumber;
        }

        if (isProtocolsGrid) {
          payload.pageSize = 20;
          payload.pageNumber = 1;
        }

        const response = await reportServiceFunction(payload, download);


        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        posthog.capture("report generated", {
          report_type: pageTitle,
          page_grid: pageGrid,
          is_download: download,
          file_type: fileType || null,
          vehicle_id: vehicleId,
          group_id: groupId,
        });

        if (!download) {
          const normalizedResponse = response ?? [];
          // A partir daqui quem encerra o loading é handleGridRendered,
          // chamado pelo GridReport ao terminar de montar (só então se
          // valida se a resposta veio vazia e se exibe "Não há dados").
          willMountGrid = true;
          setGridInfo(normalizedResponse);
        } else if (response && fileType) {
          const downloaded = downloadService.downloadApiFile(
            response,
            fileType,
            pageTitle
          );

          if (!downloaded) {
            downloadService.downloadBase64File(response, fileType, pageTitle);
          }

          setToastParams(
            JSON.stringify({
              show: true,
              title: t("REPORTS.REPORT_REQUEST"),
              body: "Download realizado com sucesso.",
              color: "Success",
            })
          );
        } else {
          setGridInfo([]);
        }
      } catch (error) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("REPORTS.FILTER_VALIDATION"),
            body: `${t("REPORTS.ERROR_FETCHING_REPORT")} ${pageTitle}`,
            color: "warning",
          })
        );
        console.error(`${t("REPORTS.ERROR_FETCHING_REPORT")} ${pageTitle}:`, error);
      } finally {
        if (currentRequestId === requestIdRef.current && !willMountGrid) {
          setIsLoading(false);
        }
      }
    } else {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("REPORTS.FILTER_VALIDATION"),
          body: t("REPORTS.SELECT_VEHICLE"),
          color: "warning",
        })
      );
    }
  };

  function validatePageFilters(pageTitle, vehicleId, userId) {
    switch (pageTitle) {
      // Não funciona com vehicleId = 0
      case "Distância Percorrida":
      case "Controle de Uso":
        return vehicleId && vehicleId != 0;
      // Funciona com vehicleId = 0
      case "Posicionamento":
      case "Deslocamento":
      case "Controle de Velocidade":
      case "Veículos em Visitas":
      case "Veículos em visitas":
      case "Protocolos de Atendimento":
        return vehicleId !== null && vehicleId !== undefined;
      // Só precisa do userId preenchido
      case "Logs":
        return userId != null;
      default:
        return false;
    }
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      <VehicleFiltersComponent
        handleClearGrid={handleClearGrid}
        handleCallback={handleGenerateReport}
        page={pageGrid}
        isLoading={isLoading}
      />
      {gridInfo !== null && (
        // Mantém montado durante o loading para o Syncfusion terminar de
        // montar/paginar (dataBound) antes de revelar. `visibility:hidden`
        // preserva as dimensões do container — `display:none` zera a
        // largura/altura e faz o Grid renderizar como se estivesse vazio.
        <div
          style={
            isLoading
              ? { position: "absolute", visibility: "hidden", width: "100%", pointerEvents: "none" }
              : undefined
          }
        >
          <GridReport
            gridData={gridInfo}
            pageGrid={pageGrid}
            onGridRendered={handleGridRendered}
          />
        </div>
      )}
      {isLoading && <Spinner />}
    </div>
  );
}
